type EventCallback = (...args: any[]) => void;

class EventBus {
  private static instance: EventBus;
  private listeners: { [key: string]: EventCallback[] } = {};
  private logoutInProgress: boolean = false;
  /** When set, 401 auto-logout is suppressed until this timestamp */
  private logoutGraceUntil: number = 0;

  private constructor() { }

  static getInstance(): EventBus {
    if (!EventBus.instance) {
      EventBus.instance = new EventBus();
    }
    return EventBus.instance;
  }

  /**
   * Suppress 401 auto-logout for the given duration (ms).
   * Use before operations that invalidate the user's session but need
   * to show a countdown/message before redirecting (e.g., org encryption
   * setup, self password reset).
   */
  startLogoutGracePeriod(durationMs: number) {
    this.logoutGraceUntil = Date.now() + durationMs;
  }

  /** Clear the grace period (e.g., when the intentional logout fires). */
  clearLogoutGracePeriod() {
    this.logoutGraceUntil = 0;
  }

  /** Check if currently inside a logout grace period. */
  isInGracePeriod(): boolean {
    return Date.now() < this.logoutGraceUntil;
  }

  subscribe(event: string, callback: EventCallback) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);

    // Return unsubscribe function
    return () => {
      this.listeners[event] = this.listeners[event].filter(
        (cb) => cb !== callback
      );
    };
  }

  emit(event: string, ...args: any[]) {
    // Prevent multiple UNAUTHORIZED_ERROR events from triggering logout loops
    if (event === "UNAUTHORIZED_ERROR") {
      // During grace period, suppress 401 auto-logout entirely
      if (Date.now() < this.logoutGraceUntil) {
        console.log("[EventBus] Inside logout grace period, ignoring UNAUTHORIZED_ERROR");
        return;
      }
      if (this.logoutInProgress) {
        console.log("[EventBus] Logout already in progress, ignoring duplicate UNAUTHORIZED_ERROR event");
        return;
      }
      this.logoutInProgress = true;

      // Reset the flag after a delay to allow future legitimate logout attempts
      setTimeout(() => {
        this.logoutInProgress = false;
      }, 5000);
    }

    if (this.listeners[event]) {
      this.listeners[event].forEach((callback) => callback(...args));
    }
  }
  resetLogoutFlag() {
    this.logoutInProgress = false;
  }
}

export const eventBus = EventBus.getInstance();
